import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import * as Sentry from "@sentry/react";
import { supabase } from "@/integrations/supabase/client";
import { SubscriptionProvider, useSubscription } from "@/contexts/SubscriptionContext";
import Layout from "./components/Layout";
import Landing from "./pages/Landing";
import Dashboard from "./pages/Dashboard";
import Practice from "./pages/Practice";
import PracticeTalk from "./pages/PracticeTalk";
import Review from "./pages/Review";
import ProgressPage from "./pages/Progress";
import Upgrade from "./pages/Upgrade";
import Auth from "./pages/Auth";
import Ajuda from "./pages/Ajuda";
import AccountDeletion from "./pages/AccountDeletion";
import NotFound from "./pages/NotFound";
import Privacy from "./pages/Privacy";

const queryClient = new QueryClient();

const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
  const location = useLocation();
  const [session, setSession] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setLoading(false);
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
    });

    return () => subscription.unsubscribe();
  }, []);

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
    </div>;
  }

  if (!session) {
    const next = `${location.pathname}${location.search}`;
    const params = new URLSearchParams({ next });
    return <Navigate to={`/auth?${params.toString()}`} replace />;
  }

  return <>{children}</>;
};

const PaidRoute = ({ children }: { children: React.ReactNode }) => {
  const { isPaid, loading } = useSubscription();

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
    </div>;
  }

  if (!isPaid) {
    return <Navigate to="/app/upgrade" replace />;
  }

  return <>{children}</>;
};

const App = () => (
  <Sentry.ErrorBoundary fallback={<div className="min-h-screen flex items-center justify-center text-gray-500">Algo deu errado. Tente recarregar a página.</div>}>
    <QueryClientProvider client={queryClient}>
      <SubscriptionProvider>
        <TooltipProvider>
          <Toaster />
          <Sonner />
          <BrowserRouter>
            <Routes>
              <Route path="/" element={<Landing />} />
              <Route path="/auth" element={<Auth />} />
              <Route path="/auth/login" element={<Navigate to="/auth?tab=signin" replace />} />
              <Route path="/auth/register" element={<Navigate to="/auth?tab=signup" replace />} />
              <Route path="/privacidade" element={<Privacy />} />
              <Route path="/excluir-conta" element={<AccountDeletion />} />
              <Route path="/app" element={<ProtectedRoute><Layout /></ProtectedRoute>}>
                <Route index element={<Dashboard />} />
                <Route path="practice" element={<Practice />} />
                <Route path="progress" element={<ProgressPage />} />
                <Route path="upgrade" element={<Upgrade />} />
                <Route path="situations" element={<Navigate to="/app/practice" replace />} />
                <Route path="situations/:id" element={<Navigate to="/app/practice" replace />} />
                <Route path="create" element={<Navigate to="/app/practice" replace />} />
                <Route path="chat" element={<Navigate to="/app/practice" replace />} />
                <Route path="conversation" element={<Navigate to="/app/practice" replace />} />
                <Route path="conversation/:situationId" element={<Navigate to="/app/practice" replace />} />
              </Route>
              <Route path="/app/practice/:slug" element={<ProtectedRoute><PaidRoute><PracticeTalk /></PaidRoute></ProtectedRoute>} />
              <Route path="/app/ajuda" element={<ProtectedRoute><PaidRoute><Ajuda /></PaidRoute></ProtectedRoute>} />
              <Route path="/app/review/:sessionId" element={<ProtectedRoute><Review /></ProtectedRoute>} />
              <Route path="*" element={<NotFound />} />
            </Routes>
          </BrowserRouter>
        </TooltipProvider>
      </SubscriptionProvider>
    </QueryClientProvider>
  </Sentry.ErrorBoundary>
);

export default App;
